import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useCallback } from 'react';

import Popup from 'components/Popup/Popup';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';

import { useWipColumnPicker } from '../../hooks/useWipColumnPicker';

import WipColumnPicker from './WipColumnPicker';
import { getPopupContentStyle } from './WIPColumnPickerPopup.styled';

type WIPColumnPickerPopupPropsT = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Modal wrapper around the WIP "Customize Columns" picker. Uses the shared Chirp `Popup`
 * component (the same dragable-resizable shell Tickets uses) so the modal chrome, sizing, and
 * close behavior match the rest of the app. Title row includes the same drag-and-drop info
 * tooltip the Tickets column picker shows.
 */
const WIPColumnPickerPopup: FC<WIPColumnPickerPopupPropsT> = ({ isOpen, onClose }): JSX.Element => {
  const {
    columnOrder,
    handleOrderChange,
    handleVisibilityToggle,
    handleResetConfig,
    handleSaveConfig,
  } = useWipColumnPicker();

  const handleSave = useCallback((): void => {
    handleSaveConfig();
    onClose();
  }, [handleSaveConfig, onClose]);

  return (
    <Popup
      showCloseBtn
      modalTitle={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Customize Columns</Typography>
          <Tooltip
            arrow
            title={
              <Box>
                <Box display="flex" alignItems="flex-start" gap={0.5} mb={0.5}>
                  <Typography variant="caption">&bull;</Typography>
                  <Typography variant="caption">
                    Drag and drop columns to adjust their order.
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <Typography variant="caption">&bull;</Typography>
                  <Typography variant="caption">
                    Use the switch to hide and show columns.
                  </Typography>
                </Box>
              </Box>
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: '1.25rem',
                color: (theme): string => theme.palette.text.secondary,
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      }
      popupId={ScrollLockIdE.ColumnPickerPopup}
      contentStyle={getPopupContentStyle()}
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      size={ModalSizeE.COLUMN_PICKER}
      data-testid="wip-column-picker-popup"
    >
      <WipColumnPicker
        columns={columnOrder}
        onOrderChange={handleOrderChange}
        onVisibilityToggle={handleVisibilityToggle}
        onResetConfig={handleResetConfig}
        onSaveConfig={handleSave}
      />
    </Popup>
  );
};

export default WIPColumnPickerPopup;
